import { useDeleteSession } from "@/api/auth/queries";
import { AuthLoader } from "@/features/auth/components/auth-loader";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import ENV from "@/utils/env";
import { useEffect } from "react";

/*
 * SignoutRoute component
 *
 * Performs a full-page redirect to the Auth0 signout endpoint.
 */
export const SignoutRoute = () => {
  /* Hooks */
  const { mutate: deleteSession } = useDeleteSession();
  const isAuth0RolloutEnabled = useFeatureFlag("auth0Rollout");

  return isAuth0RolloutEnabled ? <Auth0SignoutRoute deleteSession={deleteSession} /> : <LegacySignoutRoute deleteSession={deleteSession} />;
};

/**
 * buildAuth0LogoutUrl function
 *
 * Builds the Auth0 logout URL for the rollout flow.
 *
 * @returns The full Auth0 logout URL.
 */
export const buildAuth0LogoutUrl = () => {
  const params = new URLSearchParams({
    client_id: ENV.AUTH0_CLIENT_ID,
    returnTo: ENV.AUTH0_LOGOUT_URL,
  });

  return `${ENV.AUTH0_DOMAIN}/v2/logout?${params.toString()}`;
};

/**
 * buildLegacySignoutUrl function
 *
 * Builds the legacy signout URL used by the iframe auth flow.
 *
 * @returns The legacy signout URL.
 */
export const buildLegacySignoutUrl = () => {
  return `${ENV.VITE_AUTH_URL}/signin`;
};

/**
 * Auth0SignoutRoute component
 *
 * Deletes the session and then redirects to Auth0 logout.
 */
const Auth0SignoutRoute = ({ deleteSession }: { deleteSession: ReturnType<typeof useDeleteSession>["mutate"] }) => {
  /* Side Effects */
  useEffect(() => {
    // Delete session first, then redirect
    deleteSession(undefined, {
      onSuccess: () => {
        // Redirect to Auth0 for logout
        // This will clear the Auth0 session and then redirect back to our app.
        window.location.replace(buildAuth0LogoutUrl());
      },
    });
  }, [deleteSession]);

  return <AuthLoader loaderText="Signing out..." />;
};

/**
 * LegacySignoutRoute component
 *
 * Deletes the session and then redirects to the legacy auth app.
 */
const LegacySignoutRoute = ({ deleteSession }: { deleteSession: ReturnType<typeof useDeleteSession>["mutate"] }) => {
  /* Side Effects */
  useEffect(() => {
    // Delete session first, then redirect
    deleteSession(undefined, {
      onSuccess: () => {
        window.location.href = buildLegacySignoutUrl();
      },
    });
  }, [deleteSession]);

  return <AuthLoader loaderText="Signing out..." />;
};
